/* Aevex Performance Optimisation — lazy-load, CWV monitor, service worker, resource hints */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* 1. LAZY LOADING — native + IntersectionObserver fallback            */
  /* ------------------------------------------------------------------ */
  var supportsNativeLazy = 'loading' in HTMLImageElement.prototype;

  function enhanceLazyImages() {
    var images = document.querySelectorAll('img:not([loading="eager"]):not(.skip-lazy)');
    if (supportsNativeLazy) {
      images.forEach(function (img) {
        if (!img.hasAttribute('loading')) img.setAttribute('loading', 'lazy');
        if (!img.hasAttribute('decoding')) img.setAttribute('decoding', 'async');
      });
    } else if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var img = entry.target;
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
          }
          if (img.dataset.srcset) {
            img.srcset = img.dataset.srcset;
            img.removeAttribute('data-srcset');
          }
          observer.unobserve(img);
        });
      }, { rootMargin: '200px 0px', threshold: 0.01 });

      images.forEach(function (img) { observer.observe(img); });
    }
  }

  /* ------------------------------------------------------------------ */
  /* 2. IMAGE FORMAT DETECTION — WebP / AVIF fallback                   */
  /* ------------------------------------------------------------------ */
  function supportsFormat(format) {
    var canvas = document.createElement('canvas');
    if (canvas.toDataURL) {
      return canvas.toDataURL('image/' + format).indexOf('data:image/' + format) === 0;
    }
    return false;
  }

  var webpSupported = supportsFormat('webp');
  var avifSupported = false;

  try {
    avifSupported = window.createImageBitmap !== undefined;
  } catch (_) { /* noop */ }

  document.documentElement.classList.add(
    avifSupported ? 'avif' : webpSupported ? 'webp' : 'no-modern-img'
  );

  /* ------------------------------------------------------------------ */
  /* 3. FONT LOADING — prevent FOIT                                     */
  /* ------------------------------------------------------------------ */
  if ('fonts' in document) {
    Promise.all([
      document.fonts.load('16px Inter'),
      document.fonts.load('16px "Space Grotesk"')
    ]).then(function () {
      document.documentElement.classList.add('fonts-loaded');
    }).catch(function () {
      document.documentElement.classList.add('fonts-loaded');
    });
  }

  /* ------------------------------------------------------------------ */
  /* 4. RESOURCE HINTS — preload critical assets below the fold         */
  /* ------------------------------------------------------------------ */
  function addResourceHint(url, rel, as) {
    if (document.querySelector('link[href="' + url + '"]')) return;
    var link = document.createElement('link');
    link.rel = rel;
    link.href = url;
    if (as) link.setAttribute('as', as);
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  }

  function preloadAboveFoldImages() {
    var images = document.querySelectorAll('.hero img, [data-above-fold] img');
    images.forEach(function (img) {
      if (img.currentSrc || img.src) {
        addResourceHint(img.currentSrc || img.src, 'preload', 'image');
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* 5. SERVICE WORKER — offline cache for static assets                */
  /* ------------------------------------------------------------------ */
  if ('serviceWorker' in navigator && location.protocol === 'https:') {
    window.addEventListener('load', function () {
      navigator.serviceWorker.register('/sw.js').catch(function () {
        /* SW not available — acceptable */
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* 6. THIRD-PARTY SCRIPT DEFERRAL                                     */
  /* ------------------------------------------------------------------ */
  function deferThirdPartyScripts() {
    var deferSelectors = [
      'script[src*="google-analytics"]',
      'script[src*="googletagmanager"]',
      'script[src*="facebook"]',
      'script[src*="tiktok"]',
      'script[src*="snap."]',
      'script[src*="pinterest"]'
    ];

    document.querySelectorAll(deferSelectors.join(',')).forEach(function (script) {
      if (!script.defer && !script.async) {
        script.setAttribute('defer', '');
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* 7. CORE WEB VITALS MONITORING                                      */
  /* ------------------------------------------------------------------ */
  var cwvMetrics = { lcp: null, fid: null, cls: 0, clsEntries: 0 };

  // LCP
  function observeLCP() {
    if (!('PerformanceObserver' in window)) return;
    try {
      var observer = new PerformanceObserver(function (entryList) {
        var entries = entryList.getEntries();
        var last = entries[entries.length - 1];
        cwvMetrics.lcp = Math.round(last.startTime);
      });
      observer.observe({ type: 'largest-contentful-paint', buffered: true });
    } catch (_) { /* not supported */ }
  }

  // FID
  function observeFID() {
    if (!('PerformanceEventTiming' in window)) return;
    try {
      var observer = new PerformanceObserver(function (entryList) {
        var entries = entryList.getEntries();
        entries.forEach(function (entry) {
          if (entry.entryType === 'first-input') {
            cwvMetrics.fid = Math.round(entry.processingStart - entry.startTime);
          }
        });
      });
      observer.observe({ type: 'first-input', buffered: true });
    } catch (_) { /* not supported */ }
  }

  // CLS
  function observeCLS() {
    if (!('PerformanceObserver' in window)) return;
    try {
      var observer = new PerformanceObserver(function (entryList) {
        entryList.getEntries().forEach(function (entry) {
          if (!entry.hadRecentInput) {
            cwvMetrics.cls += entry.value;
            cwvMetrics.clsEntries++;
          }
        });
      });
      observer.observe({ type: 'layout-shift', buffered: true });
    } catch (_) { /* not supported */ }
  }

  /* ------------------------------------------------------------------ */
  /* 8. PERFORMANCE BUDGET ALERTS                                       */
  /* ------------------------------------------------------------------ */
  var BUDGETS = {
    lcp: 2500,   // ms
    fid: 100,    // ms
    cls: 0.1     // score
  };

  function checkPerformanceBudgets() {
    var issues = [];
    if (cwvMetrics.lcp !== null && cwvMetrics.lcp > BUDGETS.lcp) {
      issues.push('LCP: ' + cwvMetrics.lcp + 'ms (budget: ' + BUDGETS.lcp + 'ms)');
    }
    if (cwvMetrics.fid !== null && cwvMetrics.fid > BUDGETS.fid) {
      issues.push('FID: ' + cwvMetrics.fid + 'ms (budget: ' + BUDGETS.fid + 'ms)');
    }
    if (cwvMetrics.cls > BUDGETS.cls) {
      issues.push('CLS: ' + cwvMetrics.cls.toFixed(3) + ' (budget: ' + BUDGETS.cls + ')');
    }
    if (issues.length > 0) {
      console.warn('[Aevex Performance] Budget exceeded:', issues.join(' | '));
    }
    return issues;
  }

  // Report CWV to console after page load
  window.addEventListener('load', function () {
    setTimeout(function () {
      console.log('[Aevex CWV]', JSON.stringify({
        lcp: cwvMetrics.lcp + 'ms',
        fid: cwvMetrics.fid !== null ? cwvMetrics.fid + 'ms' : 'n/a',
        cls: cwvMetrics.cls.toFixed(4)
      }));
      checkPerformanceBudgets();
    }, 3000);
  });

  /* ------------------------------------------------------------------ */
  /* 9. CONTENT-VISIBILITY AUTO-HINT for below-fold sections            */
  /* ------------------------------------------------------------------ */
  function applyContentVisibility() {
    if (!CSS.supports || !CSS.supports('content-visibility', 'auto')) return;
    var sections = document.querySelectorAll('.section:not(.section--hero):not(:first-child)');
    sections.forEach(function (section) {
      section.style.contentVisibility = 'auto';
      section.style.containIntrinsicSize = '0 500px';
    });
  }

  /* ------------------------------------------------------------------ */
  /* INIT                                                                */
  /* ------------------------------------------------------------------ */
  document.addEventListener('DOMContentLoaded', function () {
    enhanceLazyImages();
    preloadAboveFoldImages();
    deferThirdPartyScripts();
    applyContentVisibility();
  });

  observeLCP();
  observeFID();
  observeCLS();

  // Expose CWV data for analytics
  window.aevexCWV = cwvMetrics;
  window.aevexPerformanceBudget = checkPerformanceBudgets;
})();
